import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider.js';
import { useCollectionPickerOptions } from '../data/queries.js';
import { getEffectiveOcrConfig } from '../import/api.js';
import { CollectionPicker } from '../import/CollectionPicker.js';
import { isMultiShotAvailable } from '../import/multiShotShim.js';
import { OcrSetupGuide } from '../import/OcrSetupGuide.js';
import type { PageMarker } from '../import/pageMarker.js';
import { setPendingPdf } from '../import/pdfHandoff.js';
import { useOcrKeys } from '../import/queries.js';
import { isLiveViewfinderSupported, scanPages } from '../import/scanPages.js';
import { readSharedFile } from '../import/sharedFile.js';
import { uploadBatch, type UploadProgress } from '../import/uploadBatch.js';
import { useSync } from '../local/SyncProvider.js';
import { reportError } from '../sentry.js';
import { DEFAULT_FALLBACK_MODEL, loadFallbackPrefs } from '../settings/FallbackModelSection.js';
import { DEFAULT_MODEL_BY_PROVIDER } from '../settings/ocrSettings.js';

type Step = 'source' | 'review' | 'settings' | 'uploading';

const isPdf = (f: File) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');

export function ImportNewPage() {
  const { user } = useAuth();
  const { syncNow, status: syncStatus } = useSync();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Share entry: an image shared from another app lands here as `?file=<file://…>`
  // (the app group path). Read it once and seed the batch like any picked image.
  const sharedFileRan = useRef(false);
  const { data: pickerOptions = [], isLoading: pickerLoading } = useCollectionPickerOptions();
  const { data: ocrKeys = [] } = useOcrKeys();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('source');
  const [files, setFiles] = useState<File[]>([]);
  // Capture-time page markers from the camera, keyed by File identity (rather
  // than index) so removing a thumbnail can't desync them. Files added by the
  // picker / drag-drop simply have no entry and fall back to RECIPE.
  const [markerByFile, setMarkerByFile] = useState<Map<File, PageMarker>>(() => new Map());
  const [previews, setPreviews] = useState<string[]>([]);
  const [name, setName] = useState(() => `Imported ${new Date().toLocaleDateString()}`);
  // `?collection=<id>` preselects the target cookbook (deep link from a
  // cookbook, and what the App Store screenshot driver uses).
  const [targetCollectionId, setTargetCollectionId] = useState<string>(
    () => params.get('collection') ?? '',
  );
  // 'ocr-first' = current behavior (worker OCRs each page, user merges
  // after). 'group-first' = user clicks splits between pages, then
  // each group OCRs as one call. Default stays 'ocr-first' so existing
  // flows are untouched.
  const [importMode, setImportMode] = useState<'ocr-first' | 'group-first'>('ocr-first');
  const [provider, setProvider] = useState<'gemini' | 'openai-compatible'>('gemini');
  const [model, setModel] = useState('');
  const [fallbackProvider, setFallbackProvider] = useState<'' | 'gemini' | 'openai-compatible'>(
    () => loadFallbackPrefs().provider,
  );
  const [fallbackModel, setFallbackModel] = useState(() => loadFallbackPrefs().model);
  // Effective OCR config: own prefs, else the household's shared config.
  // The prompt + keyOwnerId aren't edited in this form — they're snapshotted
  // onto the batch so the worker uses them (prompt) / bills the right account.
  const [ocrPrompt, setOcrPrompt] = useState<string | null>(null);
  const [keyOwnerId, setKeyOwnerId] = useState<string | null>(null);
  const [configSource, setConfigSource] = useState<'own' | 'household' | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [multiShotReady, setMultiShotReady] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Seed defaults from the user's saved server-side prefs, falling back
  // to whichever provider has a key configured, then a hardcoded default.
  useEffect(() => {
    let cancelled = false;
    // Effective config: the user's own prefs+key, else the household's
    // shared config (provider/model/prompt/fallback + borrowed key).
    void getEffectiveOcrConfig()
      .then((cfg) => {
        if (cancelled) return;
        setNeedsSetup(cfg === null);
        if (cfg) {
          setProvider(cfg.provider);
          setModel(cfg.model || DEFAULT_MODEL_BY_PROVIDER[cfg.provider]);
          setOcrPrompt(cfg.prompt);
          setConfigSource(cfg.source);
          setKeyOwnerId(cfg.source === 'household' ? cfg.keyOwnerId : null);
          if (cfg.source === 'household') {
            // Household config carries its own fallback choice.
            setFallbackProvider(cfg.fallbackProvider ?? '');
            setFallbackModel(cfg.fallbackModel ?? '');
            return;
          }
        } else if (ocrKeys.length > 0) {
          const k = ocrKeys[0]!;
          const p = k.provider === 'openai-compatible' ? 'openai-compatible' : 'gemini';
          setProvider(p);
          setModel(DEFAULT_MODEL_BY_PROVIDER[p]);
        } else {
          setModel(DEFAULT_MODEL_BY_PROVIDER.gemini);
        }
        // Own/own-default path: fallback still lives in localStorage.
        const fallback = loadFallbackPrefs();
        setFallbackProvider(fallback.provider);
        setFallbackModel(fallback.model);
      })
      .catch(() => {
        if (!cancelled) setModel(DEFAULT_MODEL_BY_PROVIDER.gemini);
      });
    return () => {
      cancelled = true;
    };
  }, [ocrKeys]);

  useEffect(() => {
    void (async () => setMultiShotReady(await isMultiShotAvailable()))();
  }, []);

  // Pull in a shared image (iOS share → /import/new?file=…). Reads the bytes
  // out of the app group container and seeds the batch, jumping to review.
  useEffect(() => {
    const fileUrl = params.get('file');
    if (!fileUrl || sharedFileRan.current) return;
    sharedFileRan.current = true;
    void (async () => {
      try {
        const file = await readSharedFile(fileUrl);
        // Share routing sends PDFs to /import/pdf, but a mislabelled share
        // must not sneak past the "one recipe or a cookbook?" question.
        if (isPdf(file)) {
          setPendingPdf(file);
          navigate('/import/pdf');
          return;
        }
        // Inline the seed (rather than addFiles) so this effect depends only on
        // stable setters — keeps react-hooks/exhaustive-deps quiet.
        setFiles((cur) => [...cur, file]);
        setStep('review');
      } catch {
        setError("Couldn't read the shared image. Open the app and share again.");
      }
    })();
  }, [params, navigate]);

  // Generate object URL previews, revoking the old ones whenever the
  // file list changes.
  useEffect(() => {
    const urls = files.map((f) => (f.type === 'application/pdf' ? '' : URL.createObjectURL(f)));
    setPreviews(urls);
    return () => {
      for (const u of urls) if (u) URL.revokeObjectURL(u);
    };
  }, [files]);

  // Warn the user before navigation while an upload is in flight.
  useEffect(() => {
    function handler(e: BeforeUnloadEvent) {
      if (step === 'uploading') {
        e.preventDefault();
        e.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [step]);

  const totalSizeMb = useMemo(
    () => (files.reduce((acc, f) => acc + f.size, 0) / 1_048_576).toFixed(1),
    [files],
  );

  const sourceKind = files.some(isPdf) ? 'PDF' : 'IMAGES';

  function addFiles(list: FileList | File[] | null | undefined) {
    if (!list) return;
    const arr = Array.from(list);
    if (arr.length === 0) return;
    // A PDF is ambiguous (one printed recipe vs a scanned cookbook), and the
    // answer must not depend on which door the user used. Hand it to the PDF
    // page, which owns that question and both outcomes.
    const pdf = arr.find(isPdf);
    if (pdf) {
      setPendingPdf(pdf);
      navigate('/import/pdf');
      return;
    }
    setFiles((cur) => [...cur, ...arr]);
    setStep('review');
  }

  async function onTakePhotos() {
    try {
      const captured = await scanPages();
      if (captured.length > 0) {
        // Camera scans are organized on the grouping screen, same as the
        // dedicated Scan-pages flow — and, as there, the shutter's ▤ Contents
        // choice rides along to the item rows while ⛓ Join stays undoable.
        setImportMode('group-first');
        setMarkerByFile((cur) => {
          const next = new Map(cur);
          for (const p of captured) next.set(p.file, p.marker);
          return next;
        });
        addFiles(captured.map((p) => p.file));
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }

  async function startImport() {
    if (!user) return;
    if (files.length === 0) return;
    setError(undefined);
    setStep('uploading');
    try {
      const result = await uploadBatch(
        {
          ownerId: user.id,
          name: name.trim() || `Imported ${new Date().toLocaleDateString()}`,
          targetCollectionId: targetCollectionId || null,
          defaultProvider: provider,
          defaultModel: model.trim() || DEFAULT_MODEL_BY_PROVIDER[provider],
          defaultPrompt: ocrPrompt,
          fallbackProvider: fallbackProvider || null,
          fallbackModel: fallbackProvider ? fallbackModel.trim() || DEFAULT_FALLBACK_MODEL : null,
          fallbackEndpoint: fallbackProvider ? loadFallbackPrefs().endpoint.trim() || null : null,
          keyOwnerId,
          sourceKind,
          files,
          markers: files.map((f) => ({
            kind: markerByFile.get(f)?.kind ?? 'RECIPE',
            joinsPrevious: false,
          })),
          awaitGrouping: importMode === 'group-first',
        },
        setProgress,
      );
      // Group-first lands on the grouping UI; ocr-first lands on the
      // usual batch board where OCR is already churning.
      await syncNow();
      if (importMode === 'group-first') {
        const initialMerges = files.flatMap((f, i) =>
          i > 0 && markerByFile.get(f)?.joinsPrevious ? [i - 1] : [],
        );
        navigate(`/import/${result.batchId}/group`, { state: { initialMerges } });
      } else {
        navigate(`/import/${result.batchId}`);
      }
    } catch (e) {
      reportError(e, { operation: 'batch_upload', tags: { source: 'photos' } });
      setError((e as Error).message);
      setStep('settings');
    }
  }

  if (step === 'uploading') {
    const phaseLabel =
      progress?.phase === 'preparing'
        ? 'Preparing pages'
        : progress?.phase === 'uploading'
          ? 'Uploading'
          : progress?.phase === 'finalizing'
            ? 'Finalizing'
            : 'Done';
    const pct =
      progress && progress.total > 0
        ? Math.min(100, Math.round((progress.done / progress.total) * 100))
        : 0;
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Uploading…</h1>
        <p className="text-sm text-stone-600 dark:text-stone-400">
          Don't close this tab. Large batches take a few minutes.
        </p>
        <div>
          <div className="flex justify-between text-sm text-stone-700 dark:text-stone-300">
            <span>{phaseLabel}</span>
            <span>
              {progress?.done ?? 0} / {progress?.total ?? 0}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-stone-200 dark:bg-stone-700">
            <div className="h-full bg-stone-900 dark:bg-stone-100" style={{ width: `${pct}%` }} />
          </div>
          {progress?.message && (
            <div className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              {progress.message}
            </div>
          )}
          {progress?.phase === 'finalizing' && syncStatus === 'syncing' && (
            <div className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              Saving batch locally while your library syncs in the background…
            </div>
          )}
        </div>
        {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Upload photos</h1>

      {needsSetup && <OcrSetupGuide />}
      {configSource === 'household' && (
        <div
          data-testid="ocr-household-note"
          className="rounded-md border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-sm text-emerald-900 dark:text-emerald-200"
        >
          Your household set up OCR for you — you're ready to import.
        </div>
      )}

      {step === 'source' && (
        <section className="space-y-4">
          <div
            onDragEnter={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`rounded-lg border-2 border-dashed p-8 text-center transition ${
              dragOver
                ? 'border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-900'
                : 'border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-900 hover:bg-stone-50 dark:hover:bg-stone-900'
            }`}
          >
            <p className="text-sm text-stone-700 dark:text-stone-300">
              Drag & drop images or a PDF here
            </p>
            <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
              …or pick a source below
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SourceButton label="Choose images" onClick={() => fileInputRef.current?.click()} />
            <SourceButton label="Upload PDF" onClick={() => navigate('/import/pdf')} />
            {(isLiveViewfinderSupported() || multiShotReady) && (
              <SourceButton label="Scan pages" onClick={onTakePhotos} />
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}
        </section>
      )}

      {(step === 'review' || step === 'settings') && (
        <section
          onDragEnter={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`space-y-4 rounded-lg transition ${
            dragOver
              ? 'bg-stone-50 dark:bg-stone-900 ring-2 ring-stone-900 dark:ring-stone-100'
              : ''
          }`}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {files.length} {files.length === 1 ? 'file' : 'files'} · {totalSizeMb} MB
            </h2>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
              >
                + Add more
              </button>
              <button
                type="button"
                onClick={() => {
                  setFiles([]);
                  setStep('source');
                }}
                className="text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
              >
                Clear
              </button>
            </div>
          </div>
          <p className="text-xs text-stone-500 dark:text-stone-400">
            Drop more files anywhere on this panel to add them.
          </p>
          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="relative aspect-square overflow-hidden rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900"
              >
                {previews[i] ? (
                  <img src={previews[i]} alt={f.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-stone-500 dark:text-stone-400">
                    PDF
                  </div>
                )}
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  onClick={() => setFiles((cur) => cur.filter((_, idx) => idx !== i))}
                  className="absolute right-1 top-1 rounded-full bg-white/90 dark:bg-stone-900/90 px-1.5 text-xs leading-tight text-stone-700 dark:text-stone-300 shadow"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>

          <fieldset className="rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-3">
            <legend className="px-1 text-sm font-medium text-stone-700 dark:text-stone-300">
              When do you want to group multi-page recipes?
            </legend>
            <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <ModeOption
                checked={importMode === 'ocr-first'}
                onChange={() => setImportMode('ocr-first')}
                title="OCR each page, then merge"
                body="Worker reads every page individually. Use this when each page is its own recipe, or you're not sure yet — you can still merge pages later from the batch board."
              />
              <ModeOption
                checked={importMode === 'group-first'}
                onChange={() => setImportMode('group-first')}
                title="Group pages first, then OCR"
                body="You drop splits between pages so the worker reads each group as one recipe. One OCR call per recipe instead of per page — cheaper, and avoids mid-recipe page breaks confusing the model."
              />
            </div>
          </fieldset>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Batch name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded border border-stone-300 dark:border-stone-600 px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Target cookbook">
              <CollectionPicker
                options={pickerOptions}
                value={targetCollectionId}
                onChange={setTargetCollectionId}
                loading={pickerLoading}
              />
              {!pickerLoading && pickerOptions.length === 0 && (
                <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                  No cookbooks yet — pick "Create new cookbook" above or leave unassigned.
                </p>
              )}
            </Field>
            <Field label="Default provider">
              <select
                value={provider}
                onChange={(e) => {
                  const p = e.target.value as 'gemini' | 'openai-compatible';
                  setProvider(p);
                  if (!model) setModel(DEFAULT_MODEL_BY_PROVIDER[p]);
                }}
                className="w-full rounded border border-stone-300 dark:border-stone-600 px-3 py-2 text-sm"
              >
                <option value="gemini">Google Gemini</option>
                <option value="openai-compatible">OpenAI-compatible</option>
              </select>
            </Field>
            <Field label="Default model">
              <input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full rounded border border-stone-300 dark:border-stone-600 px-3 py-2 font-mono text-sm"
              />
            </Field>
            <Field label="Fallback provider (optional)">
              <select
                value={fallbackProvider}
                onChange={(e) =>
                  setFallbackProvider(e.target.value as '' | 'gemini' | 'openai-compatible')
                }
                className="w-full rounded border border-stone-300 dark:border-stone-600 px-3 py-2 text-sm"
              >
                <option value="">(none)</option>
                <option value="gemini">Google Gemini</option>
                <option value="openai-compatible">OpenAI-compatible</option>
              </select>
            </Field>
            {fallbackProvider && (
              <Field label="Fallback model">
                <input
                  value={fallbackModel}
                  onChange={(e) => setFallbackModel(e.target.value)}
                  placeholder={DEFAULT_MODEL_BY_PROVIDER[fallbackProvider]}
                  className="w-full rounded border border-stone-300 dark:border-stone-600 px-3 py-2 font-mono text-sm"
                />
              </Field>
            )}
          </div>

          {error && <p className="text-sm text-red-700 dark:text-red-300">{error}</p>}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={startImport}
              disabled={files.length === 0}
              className="rounded-md bg-stone-900 dark:bg-stone-100 px-4 py-2 text-sm font-medium text-white dark:text-stone-900 hover:bg-stone-800 dark:hover:bg-stone-200 disabled:opacity-50"
            >
              Start import
            </button>
            <button
              type="button"
              onClick={() => navigate('/import')}
              className="rounded-md px-4 py-2 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
            >
              Cancel
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

function SourceButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-900 px-3 py-3 text-sm font-medium hover:bg-stone-50 dark:hover:bg-stone-900 hover:border-stone-400"
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
        {label}
      </span>
      {children}
    </label>
  );
}

function ModeOption({
  checked,
  onChange,
  title,
  body,
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  body: string;
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm ${
        checked
          ? 'border-stone-900 dark:border-stone-100 bg-stone-50 dark:bg-stone-900'
          : 'border-stone-200 dark:border-stone-700 hover:border-stone-400 dark:hover:border-stone-500'
      }`}
    >
      <input
        type="radio"
        name="import-mode"
        checked={checked}
        onChange={onChange}
        className="mt-1"
      />
      <span>
        <span className="block font-medium text-stone-900 dark:text-stone-100">{title}</span>
        <span className="mt-1 block text-xs text-stone-600 dark:text-stone-400">{body}</span>
      </span>
    </label>
  );
}
